import React from "react";
import {
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CRow
} from "@coreui/react-pro";
import { useSeo } from "../../hooks/useSeo";

interface PolicyPrivacyProps { }

export const PolicyPrivacy: React.FC<PolicyPrivacyProps> = () => {
  useSeo({
    title: "Política de privacidad | DomicilApp",
    description: "Qué datos recopila DomicilApp, para qué los usa, con quién los comparte, cómo se protege tu ubicación y cómo eliminar tu cuenta.",
  });

  return (
    <div className="policy-privacy-page bg-light py-5">
      <CContainer style={{ maxWidth: "960px" }}>

        {/* ENCABEZADO DE LA PÁGINA */}
        <div className="text-center mb-5">
          <CBadge
            color="danger"
            className="px-3 py-2 text-uppercase tracking-wider fw-bold mb-3 shadow-sm"
            style={{ backgroundColor: "#FE2445" }}
          >
            Aviso Legal & Transparencia
          </CBadge>
          <h1 className="fw-black display-5 text-dark mb-2">
            Política de Privacidad
          </h1>
          <p className="text-muted fs-6 mb-0">
            Última actualización: <strong>25 de octubre de 2026</strong>
          </p>
        </div>

        {/* CONTENEDOR PRINCIPAL */}
        <CCard className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">

          {/* INTRODUCCIÓN */}
          <CCardHeader
            className="py-4 px-4 px-md-5 border-0 text-white"
            style={{ backgroundColor: "#19202C" }}
          >
            <p className="fs-5 mb-0 lh-base text-white-50">
              En <strong className="text-white">DomicilApp</strong>, valoramos tu privacidad y nos comprometemos a proteger tus datos personales. Esta Política explica la forma en que recopilamos, utilizamos y salvaguardamos tu información al interactuar con nuestra plataforma.
            </p>
          </CCardHeader>

          <CCardBody className="p-4 p-md-5 text-secondary">
            <CRow className="g-4">

              {/* 1. INFORMACIÓN QUE RECOPILAMOS */}
              <CCol md={12}>
                <div className="p-4 rounded-3 bg-light border border-secondary border-opacity-10">
                  <h4 className="fw-bold text-dark mb-3">1. Información que recopilamos</h4>
                  <p className="mb-3">Para prestar nuestros servicios de logística y entrega, recopilamos los siguientes datos personales:</p>
                  <ul className="row row-cols-1 row-cols-sm-2 g-2 mb-0 ps-3">
                    <li className="col">👤 Nombre completo</li>
                    <li className="col">📞 Número de teléfono</li>
                    <li className="col">📍 Dirección de entrega</li>
                    <li className="col">✉️ Correo electrónico</li>
                    <li className="col">🛰️ Ubicación en tiempo real <small className="text-muted">(exclusivo repartidores)</small></li>
                    <li className="col">🗺️ Recorrido durante los pedidos en curso <small className="text-muted">(exclusivo repartidores)</small></li>
                    <li className="col">🛡️ Ubicación al confirmar un pedido <small className="text-muted">(clientes, para prevenir fraude)</small></li>
                  </ul>
                </div>
              </CCol>

              {/* 2. USO DE LA INFORMACIÓN */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-3">2. Uso de la información</h4>
                  <p className="small mb-2">Utilizamos tus datos exclusivamente para:</p>
                  <ul className="ps-3 mb-0 small text-secondary">
                    <li className="mb-2">Procesar pedidos y coordinar entregas a domicilio.</li>
                    <li className="mb-2">Brindar soporte y comunicar cambios en los pedidos.</li>
                    <li className="mb-2">Permitir el uso fluido de la aplicación.</li>
                    <li className="mb-2">Optimizar las rutas y tiempos de entrega mediante geolocalización.</li>
                    <li className="mb-2">Resolver reclamos y calcular tiempos y distancias reales de entrega con el recorrido del repartidor.</li>
                    <li className="mb-2">Prevenir pedidos falsos y proteger a los comercios y repartidores.</li>
                  </ul>
                </div>
              </CCol>

              {/* 3. COMPARTIR INFORMACIÓN */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-3">3. Compartir información</h4>
                  <p className="small text-secondary mb-2">
                    Tu información <strong>no se vende ni se comparte con terceros no autorizados</strong>. Solo se facilitan los datos indispensables para completar la entrega:
                  </p>
                  <ul className="ps-3 mb-0 small text-secondary">
                    <li className="mb-2">
                      <strong>Repartidor asignado:</strong> nombre, teléfono y dirección de entrega.
                    </li>
                    <li className="mb-0">
                      <strong>Empresa de domicilios aliada</strong> a la que pertenece el repartidor, si la hay: la <strong>dirección de entrega</strong> y la ruta hasta ella, para coordinar el pedido y dar soporte. No recibe tu nombre, teléfono, correo ni los productos de tu pedido, y solo la ve en pedidos atendidos por sus repartidores. Estas empresas actúan como encargadas del tratamiento bajo un acuerdo con DomicilApp y solo pueden usar la información para esa finalidad.
                    </li>
                  </ul>
                </div>
              </CCol>

              {/* 4. UBICACIÓN Y RECORRIDO DE LOS REPARTIDORES */}
              <CCol md={12}>
                <div className="p-4 rounded-3 bg-light border border-secondary border-opacity-10">
                  <h4 className="fw-bold text-dark mb-3">4. Ubicación y recorrido de los repartidores</h4>
                  <p className="small mb-2">
                    Si usas DomicilApp como <strong>repartidor (domiciliario)</strong>, tratamos tu ubicación de la siguiente forma:
                  </p>
                  <ul className="ps-3 mb-3 small text-secondary">
                    <li className="mb-2">
                      <strong>Cuándo se recopila:</strong> mientras estés en modo domiciliario y en turno, incluso con la app cerrada o en segundo plano. En ese tiempo verás una notificación fija en tu teléfono. Junto con la ubicación se registra su precisión (margen de error en metros).
                    </li>
                    <li className="mb-2">
                      <strong>Ubicación actual:</strong> se usa para ofrecerte pedidos cercanos y para que el cliente, el comercio y, si estás vinculado a una, tu empresa de domicilios vean el avance de la entrega.
                    </li>
                    <li className="mb-2">
                      <strong>Historial del recorrido:</strong> solo mientras tienes un pedido en curso, desde que lo aceptas hasta que lo entregas, guardamos los puntos de tu recorrido. Lo usamos para resolver reclamos sobre el pedido y para calcular tiempos y distancias reales de entrega. Lo consulta el equipo de soporte de DomicilApp y, mientras el pedido está en curso, tu empresa de domicilios ve tu recorrido y la ruta que te recomienda la app hasta la dirección de entrega.
                    </li>
                    <li className="mb-2">
                      <strong>Conservación:</strong> el historial del recorrido se elimina automáticamente a los <strong>90 días</strong>. Fuera de un pedido en curso solo conservamos tu última ubicación conocida.
                    </li>
                    <li className="mb-0">
                      <strong>Cómo dejar de compartirla:</strong> cambia de rol en la app, cierra sesión o retira el permiso de ubicación desde los ajustes de tu teléfono. Sin ese permiso no podrás recibir pedidos como domiciliario.
                    </li>
                  </ul>
                  <p className="small text-muted mb-0">
                    Antes de pedir el permiso de ubicación, la app te muestra un aviso con esta información y solo continúa si lo aceptas.
                  </p>
                </div>
              </CCol>

              {/* 5. PREVENCIÓN DE FRAUDE */}
              <CCol md={12}>
                <div className="p-4 rounded-3 bg-light border border-secondary border-opacity-10">
                  <h4 className="fw-bold text-dark mb-3">5. Prevención de pedidos falsos</h4>
                  <ul className="ps-3 mb-0 small text-secondary">
                    <li className="mb-2">
                      <strong>Qué tomamos:</strong> al confirmar un pedido usamos tu ubicación <strong>una sola vez</strong>, en primer plano, junto con su precisión y si el teléfono reporta una ubicación simulada. Antes de pedir el permiso la app te muestra un aviso.
                    </li>
                    <li className="mb-2">
                      <strong>Para qué:</strong> la comparamos con la dirección de entrega y, junto con otras señales (antigüedad de la cuenta, pedidos anteriores, valor en efectivo y cuentas que comparten el mismo teléfono), calculamos un nivel de riesgo del pedido.
                    </li>
                    <li className="mb-2">
                      <strong>Quién lo ve:</strong> el comercio solo ve el nivel de riesgo y sus motivos, <strong>nunca tu ubicación</strong>. Si el riesgo es alto, puede llamarte para confirmar el pedido.
                    </li>
                    <li className="mb-2">
                      <strong>Conservación:</strong> la ubicación se elimina automáticamente a los <strong>180 días</strong>; después solo conservamos la distancia a la dirección y el nivel de riesgo.
                    </li>
                    <li className="mb-0">
                      <strong>Si no la compartes:</strong> igual puedes hacer tu pedido; el pedido puede quedar marcado para revisión.
                    </li>
                  </ul>
                </div>
              </CCol>

              {/* 6. MÉTODOS DE PAGO */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-3">6. Métodos de pago</h4>
                  <p className="small text-secondary mb-0">
                    Actualmente los pagos se efectúan <strong>solo contra entrega</strong>. Por esta razón, <strong>no solicitamos, procesamos ni almacenamos datos bancarios o tarjetas de crédito/débito</strong> en nuestros servidores.
                  </p>
                </div>
              </CCol>

              {/* 7. SEGURIDAD */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-3">7. Seguridad de los datos</h4>
                  <p className="small text-secondary mb-0">
                    Implementamos protocolos de seguridad digitales (cifrado y buenas prácticas de tratamiento de datos) para proteger tu información personal contra accesos no autorizados o alteraciones.
                  </p>
                </div>
              </CCol>

              {/* 8. DERECHOS DEL USUARIO */}
              <CCol md={12}>
                <div className="p-4 rounded-3 bg-light border border-secondary border-opacity-10">
                  <h4 className="fw-bold text-dark mb-3">8. Derechos del usuario</h4>
                  <p className="mb-2">Como titular de los datos, tienes derecho a:</p>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <span className="badge bg-white text-dark border p-2">Acceder o modificar tus datos</span>
                    <span className="badge bg-white text-dark border p-2">Solicitar eliminación de la información</span>
                    <span className="badge bg-white text-dark border p-2">Consultar el tratamiento otorgado</span>
                  </div>
                  <p className="small text-muted mb-0">
                    Para ejercer estos derechos, envía un mensaje formal a: <a href="mailto:soporte@domicilapp.com" className="fw-bold text-decoration-none" style={{ color: "#FE2445" }}>soporte@domicilapp.com</a>.
                  </p>
                </div>
              </CCol>

              {/* 9. CAMBIOS EN ESTA POLÍTICA */}
              <CCol md={12}>
                <div className="p-3">
                  <h4 className="fw-bold text-dark mb-2">9. Cambios en esta política</h4>
                  <p className="small text-secondary mb-0">
                    Nos reservamos el derecho de actualizar esta política cuando sea necesario. Cualquier modificación sustancial será notificada a través de la aplicación o por correo electrónico.
                  </p>
                </div>
              </CCol>

              {/* 10. ELIMINACIÓN DE CUENTA (REQUERIMIENTO PLAY STORE / APP STORE) */}
              <CCol md={12}>
                <div className="p-4 rounded-4 text-white shadow-sm" style={{ backgroundColor: "#19202C" }}>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="fs-4">🗑️</span>
                    <h4 className="fw-bold text-white mb-0">10. Eliminación de cuenta</h4>
                  </div>
                  <p className="text-white-50 mb-3">
                    Los usuarios pueden solicitar la eliminación definitiva de su cuenta de DomicilApp en cualquier momento.
                  </p>
                  <div className="bg-dark bg-opacity-50 p-3 rounded-3 mb-3 border border-secondary border-opacity-25">
                    <small className="d-block text-white mb-2"><strong>Pasos para la solicitud:</strong></small>
                    <ol className="ps-3 mb-0 small text-white-50">
                      <li>Enviar un correo desde la cuenta registrada a <strong className="text-white">soporte@domicilapp.com</strong>.</li>
                      <li>Indicar como asunto: <code>"Eliminar mi cuenta"</code>.</li>
                    </ol>
                  </div>
                  <small className="text-white-50 d-block">
                    * Tras la verificación, la cuenta será desactivada por 5 días hábiles. transcurridos 90 días sin solicitud de reactivación, todos los datos asociados serán eliminados permanentemente.
                  </small>
                </div>
              </CCol>

              {/* SECCIÓN DE CONTACTO */}
              <CCol md={12} className="mt-4">
                <div className="text-center p-4 rounded-3 border-0 bg-danger bg-opacity-10">
                  <h5 className="fw-bold text-dark mb-2">📩 ¿Tienes dudas sobre tu privacidad?</h5>
                  <p className="text-muted small mb-3">Nuestro equipo de soporte está disponible para atender cualquier consulta legal o técnica.</p>
                  <a
                    href="mailto:soporte@domicilapp.com"
                    className="btn btn-danger fw-bold px-4 py-2 border-0 shadow-sm"
                    style={{ backgroundColor: "#FE2445" }}
                  >
                    Escribir a soporte@domicilapp.com
                  </a>
                </div>
              </CCol>

            </CRow>
          </CCardBody>
        </CCard>

      </CContainer>
    </div>
  );
};